<script setup lang="ts">
import type { PaymentMethodResponse } from '@@/types/api';

const props = withDefaults(
  defineProps<{
    paymentMethods: PaymentMethodResponse[];
    isLoadingMethods: boolean;
    submitLabel?: string;
    disabled?: boolean;
  }>(),
  { submitLabel: 'Submit', disabled: false },
);

const emit = defineEmits<{
  submit: [paymentMethodId: string];
  'card-added': [];
}>();

const selectedMethodId = ref<string | null>(null);
const showAddCardDialog = ref(false);

const brandLabels: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

function brandLabel(brand: string): string {
  return brandLabels[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1);
}

// Auto-select when exactly one card exists
watch(
  () => props.paymentMethods,
  (methods) => {
    if (methods.length === 1 && methods[0]) {
      selectedMethodId.value = methods[0].id;
    }
  },
  { immediate: true },
);

function handleCardAdded(paymentMethodId: string) {
  showAddCardDialog.value = false;
  selectedMethodId.value = paymentMethodId;
  emit('card-added');
}

function handleSubmit() {
  if (selectedMethodId.value) {
    emit('submit', selectedMethodId.value);
  }
}
</script>

<template>
  <!-- Loading state -->
  <div v-if="isLoadingMethods" class="d-flex justify-center py-4">
    <v-progress-circular indeterminate size="32" />
  </div>

  <!-- No saved cards: inline Stripe form -->
  <template v-else-if="paymentMethods.length === 0">
    <StripePaymentMethodForm
      :submit-label="submitLabel"
      :disabled="disabled"
      @payment-method-collected="emit('submit', $event)"
    />
  </template>

  <!-- Has saved cards: radio selection + add new -->
  <div v-else>
    <v-radio-group v-model="selectedMethodId" class="mb-2">
      <v-radio
        v-for="pm in paymentMethods"
        :key="pm.id"
        :value="pm.id"
        class="mb-1"
      >
        <template #label>
          <div class="d-flex align-center">
            <v-icon size="small" class="mr-2">mdi-credit-card</v-icon>
            <span class="font-weight-medium">{{ brandLabel(pm.brand) }}</span>
            <span class="text-medium-emphasis ml-2">
              &bull;&bull;&bull;&bull; {{ pm.last_four }}
              &middot;
              {{ pm.exp_month }}/{{ pm.exp_year }}
            </span>
          </div>
        </template>
      </v-radio>
    </v-radio-group>

    <v-btn
      variant="tonal"
      size="small"
      prepend-icon="mdi-plus"
      class="mb-4"
      @click="showAddCardDialog = true"
    >
      Add a new card
    </v-btn>

    <v-btn
      color="primary"
      block
      :disabled="!selectedMethodId || disabled"
      @click="handleSubmit"
    >
      {{ submitLabel }}
    </v-btn>

    <ClientOnly>
      <DashboardAddPaymentMethodDialog
        v-model="showAddCardDialog"
        :is-submitting="false"
        @submit="handleCardAdded"
      />
    </ClientOnly>
  </div>
</template>
